import React, { useState, useMemo, useCallback } from 'react';
import { FEATURES } from '../config/features';
import { useProjects } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ClockIcon, WarningIcon, FileIcon, BarChartIcon, BellIcon, DashboardIcon, UsersIcon, CalendarIcon, UserIcon, ChatIcon, ChevronRightIcon } from '../components/icons/Icons';
import {
  TaskStatusLabels,
  TaskStatusColors,
  TaskStatus,
  SubElement,
  AlertType,
  ProjectStatusLabels,
  ProjectStatusColors,
} from '../types';
import {
  getAllSubElements,
  getUserName,
  getUserTasks,
  MOCK_USERS,
} from '../data/mockData';
import { formatHours, formatPercentage } from '../utils/format';
import { GanttChart } from '../components/charts/GanttChart';
import { StatusDonut } from '../components/charts/StatusDonut';
import { BurnupArea } from '../components/charts/BurnupArea';
import { buildGanttRows } from '../utils/ganttHelpers';
import { Avatar } from '../components/common/Avatar';
import { AppSectionHero, AppStatCard } from '../components/common/AppSurface';
import { useQuickTask } from '../hooks/useQuickTask';
import { useChartColors, ChartColors } from '../hooks';
import './DashboardPage.css';

// Maps each task status to a slot in the validated chart palette (see
// useChartColors / --chart-* tokens). Distinct, CVD-safe hues — not near-identical
// blues — so every status is legible. ISSUES uses the reserved red status color.
// The concrete hex is resolved at render time from the theme tokens.
const statusChartColor = (status: TaskStatus, c: ChartColors): string => {
  switch (status) {
    case 'ASSIGNED':    return c.series[0];
    case 'STARTED':     return c.series[1];
    case 'IN_PROGRESS': return c.series[2];
    case 'IN_REVIEW':   return TaskStatusColors.IN_REVIEW;
    case 'PAUSED':      return c.series[3];
    case 'FINISHED':    return c.series[4];
    case 'ISSUES':      return c.issue;
  }
};

// One point on the burn-up chart: cumulative scope vs cumulative completed at a week boundary.
interface BurnupPoint {
  label: string;
  total: number;
  completed: number;
}

// Progress tier drives the workload bar color (design-system tokens via CSS class).
const workloadTier = (progress: number): 'low' | 'mid' | 'high' | 'done' =>
  progress >= 100 ? 'done' : progress >= 66 ? 'high' : progress >= 33 ? 'mid' : 'low';

// ---- Alerts ----
type AlertSeverity = 'critical' | 'warning' | 'info';

// Per-type metadata: which severity bucket it lands in and how it reads.
// Keeps the icon/color/order out of the JSX and covers all 6 AlertTypes
// (the old UI only styled LATE_COMPLETION vs. everything-else).
const ALERT_META: Record<AlertType, { severity: AlertSeverity; label: string }> = {
  TASK_OVERDUE:    { severity: 'critical', label: 'Overdue' },
  ISSUE_REPORTED:  { severity: 'critical', label: 'Issue' },
  LATE_COMPLETION: { severity: 'warning',  label: 'Late completion' },
  TASK_DUE_SOON:   { severity: 'warning',  label: 'Due soon' },
  TASK_ASSIGNED:   { severity: 'info',     label: 'Assigned' },
  NEW_COMMENT:     { severity: 'info',     label: 'Comment' },
};

const SEVERITY_ORDER: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };
const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  critical: 'Critical',
  warning: 'Warnings',
  info: 'Info',
};

const alertIcon = (type: AlertType) => {
  switch (type) {
    case 'TASK_OVERDUE':    return <WarningIcon size={16} />;
    case 'ISSUE_REPORTED':  return <WarningIcon size={16} />;
    case 'LATE_COMPLETION': return <ClockIcon size={16} />;
    case 'TASK_DUE_SOON':   return <CalendarIcon size={16} />;
    case 'TASK_ASSIGNED':   return <UserIcon size={16} />;
    case 'NEW_COMMENT':     return <ChatIcon size={16} />;
  }
};

// Compact relative time ("just now", "3h ago", "2d ago"); falls back to a date for old items.
const relativeTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMin = Math.round((Date.now() - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const DashboardPage: React.FC = () => {
  const { activeProject, getProjectAnalytics, alerts, markAlertRead, markAllAlertsRead, unreadAlertCount, allUsers } = useProjects();
  const { isProjectManager, currentUser } = useAuth();
  const chartColors = useChartColors();
  const quickTask = useQuickTask();
  const navigate = useNavigate();
  const [selectedIssueAssignee, setSelectedIssueAssignee] = useState('all');
  const [collapsedSeverities, setCollapsedSeverities] = useState<Set<AlertSeverity>>(new Set());

  const toggleSeverity = (severity: AlertSeverity) =>
    setCollapsedSeverities(prev => {
      const next = new Set(prev);
      next.has(severity) ? next.delete(severity) : next.add(severity);
      return next;
    });

  // Resolve a user id to its full record (for avatars), falling back to mock data.
  const resolveUser = useCallback(
    (userId: string) => allUsers.find(u => u.id === userId) || MOCK_USERS.find(u => u.id === userId),
    [allUsers]
  );

  // All hooks must be called before any conditional return
  const analytics = useMemo(
    () => activeProject ? getProjectAnalytics(activeProject) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeProject]
  );
  const allSubs = useMemo(
    () => activeProject ? getAllSubElements(activeProject) : [],
    [activeProject]
  );

  // Single source of truth: totals come from `analytics` so the ring, the bars,
  // and the "Overall Progress" header can never drift apart.
  const statusBreakdown = useMemo(() => {
    if (!analytics) return [];
    const total = analytics.totalTasks;
    const counts: Record<string, number> = {};
    allSubs.forEach(s => { counts[s.status] = (counts[s.status] || 0) + 1; });
    return (Object.keys(TaskStatusLabels) as TaskStatus[])
      .map(status => ({
        status,
        label: TaskStatusLabels[status],
        color: statusChartColor(status, chartColors),
        count: counts[status] || 0,
        pct: total > 0 ? ((counts[status] || 0) / total) * 100 : 0,
      }))
      .filter(item => item.count > 0);
  }, [allSubs, analytics, chartColors]);

  const issueTasks = useMemo(() => allSubs.filter(s => s.status === 'ISSUES'), [allSubs]);
  const issueAssigneeOptions = useMemo(() =>
    Array.from(new Set(issueTasks.flatMap(task => task.assignedTo)))
      .map(userId => {
        const user = allUsers.find(u => u.id === userId) || MOCK_USERS.find(u => u.id === userId);
        return { id: userId, name: user?.name || getUserName(userId) };
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
    [issueTasks, allUsers]
  );
  const filteredIssueTasks = useMemo(() =>
    selectedIssueAssignee === 'all'
      ? issueTasks
      : issueTasks.filter(task => task.assignedTo.includes(selectedIssueAssignee)),
    [issueTasks, selectedIssueAssignee]
  );
  const recentTasks = useMemo(() =>
    [...allSubs]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 6),
    [allSubs]
  );
  const teamWorkload = useMemo(() => {
    if (!activeProject) return [];
    const usersForDashboard = allUsers.length > 0 ? allUsers : MOCK_USERS;
    return usersForDashboard
      .filter(u => u.role !== 'PROJECT_MANAGER' && activeProject.teamMembers.includes(u.id))
      .map(u => {
        const tasks = getUserTasks(activeProject, u.id);
        const finished = tasks.filter(t => t.status === 'FINISHED').length;
        const issues = tasks.filter(t => t.status === 'ISSUES').length;
        const active = tasks.filter(t => t.status === 'IN_PROGRESS' || t.status === 'STARTED').length;
        const total = tasks.length;
        return { user: u, total, finished, issues, active, progress: total > 0 ? Math.round((finished / total) * 100) : 0 };
      })
      // Highest completion first; ties broken by larger workload.
      .sort((a, b) => b.progress - a.progress || b.total - a.total);
  }, [activeProject, allUsers]);

  // Burn-up: cumulative total tasks (scope) vs cumulative completed, by real week.
  // The gap between the two lines is the remaining work; the completed line
  // "burns up" toward the total. Weeks are derived from the actual data range
  // (project start / earliest task → today), not a fixed 8-bucket window.
  const burnup = useMemo(() => {
    if (!activeProject || allSubs.length === 0) {
      return { points: [] as BurnupPoint[], maxValue: 1 };
    }
    const dayMs = 1000 * 60 * 60 * 24;
    const createdAt = (t: SubElement) =>
      new Date(`${(t.startDate || t.createdAt || activeProject.startDate).slice(0, 10)}T00:00:00`).getTime();
    // Completion date = timestamp of the status change to FINISHED, else updatedAt.
    const finishedAt = (t: SubElement): number | null => {
      if (t.status !== 'FINISHED') return null;
      const entry = [...t.statusHistory]
        .filter(h => h.field === 'status' && h.newValue === 'FINISHED')
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
      return new Date(entry?.timestamp || t.updatedAt).getTime();
    };

    const createdTimes = allSubs.map(createdAt);
    const startMs = Math.min(...createdTimes);
    const start = new Date(startMs);
    start.setHours(0, 0, 0, 0);
    const nowMs = Date.now();
    const rawWeeks = Math.ceil((nowMs - start.getTime()) / (dayMs * 7)) + 1;
    const weekCount = Math.min(Math.max(rawWeeks, 2), 12); // 2..12 weeks on the axis
    const spanMs = Math.max(nowMs - start.getTime(), dayMs * 7);
    const stepMs = spanMs / (weekCount - 1);

    const finishedTimes = allSubs.map(finishedAt);
    const points: BurnupPoint[] = Array.from({ length: weekCount }, (_, i) => {
      const cutoff = start.getTime() + stepMs * i;
      const total = createdTimes.filter(t => t <= cutoff).length;
      const completed = finishedTimes.filter((t): t is number => t !== null && t <= cutoff).length;
      return {
        label: new Date(cutoff).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        total,
        completed,
      };
    });
    const maxValue = Math.max(points[points.length - 1]?.total ?? 1, 1);
    return { points, maxValue };
  }, [activeProject, allSubs]);

  const myTasks = useMemo(
    () => (!isProjectManager && activeProject && currentUser) ? getUserTasks(activeProject, currentUser.id) : [],
    [isProjectManager, activeProject, currentUser]
  );

  // Alerts grouped by severity, most-urgent group first, newest within a group first.
  const alertGroups = useMemo(() => {
    const buckets: Record<AlertSeverity, typeof alerts> = { critical: [], warning: [], info: [] };
    [...alerts]
      .sort((a, b) => {
        const sev = SEVERITY_ORDER[ALERT_META[a.type].severity] - SEVERITY_ORDER[ALERT_META[b.type].severity];
        return sev !== 0 ? sev : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      })
      .forEach(a => { buckets[ALERT_META[a.type].severity].push(a); });
    return (['critical', 'warning', 'info'] as AlertSeverity[])
      .map(severity => ({
        severity,
        items: buckets[severity],
        unread: buckets[severity].filter(a => !a.isRead).length,
      }))
      .filter(g => g.items.length > 0);
  }, [alerts]);

  // For technicians, show only their tasks summary
  if (!isProjectManager && activeProject && currentUser) {
    const finished = myTasks.filter(t => t.status === 'FINISHED').length;
    const issues = myTasks.filter(t => t.status === 'ISSUES').length;
    const inProgress = myTasks.filter(t => t.status === 'IN_PROGRESS' || t.status === 'STARTED').length;
    const assigned = myTasks.filter(t => t.status === 'ASSIGNED').length;

    return (
      <div className="dashboard-container">
        <AppSectionHero
          icon={<DashboardIcon size={28} />}
          title="My Overview"
          subtitle={activeProject.name}
          aside={
            <>
              <AppStatCard icon={<CheckCircleIcon size={20} />} label="Completed" value={finished} caption="Finished tasks" tone="mint" />
              <AppStatCard icon={<ClockIcon size={20} />} label="Pending" value={inProgress + assigned} caption="Active and assigned" tone="blue" />
            </>
          }
        />
        <div className="priority-grid row-2">
          <div className="priority-card transit">
            <div className="priority-header">
              <div className="priority-icon transit">
                <CheckCircleIcon size={20} />
              </div>
              <div>
                <span className="priority-count">{finished}</span>
                <span className="priority-label">Completed</span>
              </div>
            </div>
          </div>
          <div className="priority-card ordered">
            <div className="priority-header">
              <div className="priority-icon ordered">
                <ClockIcon size={20} />
              </div>
              <div>
                <span className="priority-count">{inProgress + assigned}</span>
                <span className="priority-label">Pending</span>
              </div>
            </div>
          </div>
        </div>
        {issues > 0 && (
          <div className="priority-grid row-1">
            <div className="priority-card problems">
              <div className="priority-header">
                <div className="priority-icon problems">
                  <WarningIcon size={20} />
                </div>
                <div>
                  <span className="priority-count">{issues}</span>
                  <span className="priority-label">Issues Reported</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="dashboard-card full-width mt-md">
          <h3 className="card-title">Quick Actions</h3>
          <button className="view-all-btn mt-sm" onClick={() => navigate('/my-tasks')}>
            Go to My Tasks →
          </button>
        </div>
      </div>
    );
  }

  if (!activeProject || !analytics) {
    return (
      <div className="dashboard-container">
        <AppSectionHero
          icon={<DashboardIcon size={28} />}
          title="Dashboard"
          subtitle="No active project selected."
        />
        <p className="text-muted-center">No active project selected.</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AppSectionHero
        icon={<DashboardIcon size={28} />}
        title="Project Dashboard"
        subtitle={<>{activeProject.name} {'\u2014'} <strong>{activeProject.code}</strong></>}
        aside={
          <>
            {quickTask.canCreate && (
              <button className="app-surface-btn" onClick={quickTask.open} title="New task (c)">+ New Task</button>
            )}
            <AppStatCard icon={<UsersIcon size={20} />} label="Team" value={activeProject.teamMembers.length} caption="Project members" tone="blue" />
            <AppStatCard icon={<BellIcon size={20} />} label="Alerts" value={unreadAlertCount} caption="Unread notifications" tone="mint" />
          </>
        }
      />
      {quickTask.modal}

      {/* Project status & progress (name/code already shown in the hero above) */}
      <div className="project-header-card">
        <div className="project-header-info">
          <span className="badge" style={{ backgroundColor: ProjectStatusColors[activeProject.status], color: '#fff' }}>
            {ProjectStatusLabels[activeProject.status]}
          </span>
          <div className="project-inline-progress">
            <span>Overall Progress</span>
            <div className="project-progress-track">
              {/* Avance ponderado detrás: incluye el trabajo en curso, por eso
                  siempre va por delante del % de tareas cerradas. */}
              <div
                className="project-progress-weighted"
                style={{ width: `${analytics.weightedProgressPercentage}%` }}
                title={`${formatPercentage(analytics.weightedProgressPercentage)} including work in progress`}
              />
              <div className="project-progress-fill" style={{ width: `${analytics.progressPercentage}%` }} />
            </div>
            <span className="project-progress-pct">{formatPercentage(analytics.progressPercentage)}</span>
            <span
              className="project-progress-weighted-pct"
              title="Weighted progress: counts partially advanced tasks, not just completed ones"
            >
              {formatPercentage(analytics.weightedProgressPercentage)} incl. in progress
            </span>
          </div>
        </div>
        <p className="project-client">{activeProject.client}</p>
      </div>

      {/* KPI Cards */}
      <div className={`priority-grid ${FEATURES.TIME_TRACKING ? 'row-4' : 'row-2'}`}>
        <div className="priority-card">
          <div className="priority-header">
            <div className="priority-icon transit">
              <FileIcon size={20} />
            </div>
            <div>
              <span className="priority-count">{analytics.totalTasks}</span>
              <span className="priority-label">Total Tasks</span>
            </div>
          </div>
          <span className="priority-caption">Across all project sections</span>
        </div>
        <div className="priority-card transit">
          <div className="priority-header">
            <div className="priority-icon transit">
              <CheckCircleIcon size={20} />
            </div>
            <div>
              <span className="priority-count">{analytics.completedTasks}</span>
              <span className="priority-label">Completed</span>
            </div>
          </div>
          <span className="priority-caption">{formatPercentage(analytics.progressPercentage)} overall completion</span>
        </div>
        {FEATURES.TIME_TRACKING && (
          <>
            <div className="priority-card ordered">
              <div className="priority-header">
                <div className="priority-icon ordered">
                  <ClockIcon size={20} />
                </div>
                <div>
                  <span className="priority-count">{formatHours(analytics.totalEstimatedHours)}</span>
                  <span className="priority-label">Est. Hours</span>
                </div>
              </div>
              <span className="priority-caption">Planned project effort</span>
            </div>
            <div className="priority-card" style={{ borderTopColor: analytics.hoursVariance > 0 ? 'var(--accent-red)' : 'var(--accent-green)' }}>
              <div className="priority-header">
                <div className="priority-icon" style={{ background: analytics.hoursVariance > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)', color: analytics.hoursVariance > 0 ? 'var(--accent-red)' : 'var(--accent-green)' }}>
                  <BarChartIcon size={20} />
                </div>
                <div>
                  <span className="priority-count">{formatHours(analytics.totalActualHours)}</span>
                  <span className="priority-label">Actual Hours</span>
                </div>
              </div>
              <span className="priority-caption">{formatHours(Math.abs(analytics.hoursVariance))} variance</span>
            </div>
          </>
        )}
      </div>

      {/* Status Breakdown */}
      <div className="dashboard-card full-width status-breakdown-card">
        <div className="status-breakdown-layout">
          <div className="status-breakdown-main">
          <h3 className="card-title">Task Status Breakdown</h3>
          <div className="status-list">
            {statusBreakdown.map(({ status, label, color, count, pct }) => {
              return (
                <div key={status} className="status-row">
                  <div className="status-badge-wrap">
                    <span className="status-dot" style={{ backgroundColor: color }} />
                    <span className="status-label">{label}</span>
                  </div>
                  <div className="status-bar-wrap">
                    <div className="payment-bar-track">
                      <div className="payment-bar-fill" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                    <span className="status-count">{count}</span>
                  </div>
                </div>
              );
            })}
          </div>
          </div>
          <StatusDonut
            total={analytics.totalTasks}
            segments={statusBreakdown.map(item => ({ key: item.status, label: item.label, value: item.count, color: item.color }))}
          />
        </div>
      </div>

      {/* Team Workload */}
      <div className="dashboard-card full-width team-workload-card">
        <div className="team-workload-layout">
          <div className="team-workload-section">
            <h3 className="card-title">Team Workload</h3>
            <div className="team-workload-list">
              {teamWorkload.map(tw => (
                <div key={tw.user.id} className="team-workload-row">
                  <div className="tw-member-info">
                    <Avatar user={tw.user} size={34} className="team-avatar" />
                    <div className="tw-member-text">
                      <span className="tw-member-name">{tw.user.name}</span>
                      <span className="tw-member-tasks">{tw.finished}/{tw.total} tasks</span>
                    </div>
                  </div>
                  <div className="team-workload-metrics">
                    {tw.active > 0 && <span className="metric-active">{tw.active} active</span>}
                    {tw.issues > 0 && <span className="metric-issues">{tw.issues} issues</span>}
                    {tw.active === 0 && tw.issues === 0 && <span className="metric-clear">On track</span>}
                  </div>
                  <div className="team-progress-wrap">
                    <div className="payment-bar-track">
                      <div className={`payment-bar-fill tier-${workloadTier(tw.progress)}`} style={{ width: `${tw.progress}%` }} />
                    </div>
                    <span className="team-progress-pct">{tw.progress}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="team-timeline-section">
            <div className="team-timeline-header">
              <div>
                <h3 className="card-title">Completion Burn-up</h3>
                <p>Completed tasks vs total scope over time</p>
              </div>
              <div className="team-timeline-legend">
                <span><i style={{ background: chartColors.muted }} />Total Scope</span>
                <span><i style={{ background: chartColors.green }} />Completed</span>
              </div>
            </div>
            <div className="team-timeline-chart-rc">
              <BurnupArea points={burnup.points} />
            </div>
          </div>
        </div>
      </div>

      {/* Issues Card */}
      {issueTasks.length > 0 && (
        <div className="dashboard-card full-width problems-card issues-module">
          <div className="issues-header">
            <div>
              <h3 className="card-title issues-title">
                <WarningIcon size={18} />
                Active Issues
                <span className="issues-count">{filteredIssueTasks.length} of {issueTasks.length}</span>
              </h3>
              <p className="issues-subtitle">Filter open issues by assigned employee.</p>
            </div>
            <label className="issues-filter">
              <span>Employee</span>
              <select value={selectedIssueAssignee} onChange={e => setSelectedIssueAssignee(e.target.value)}>
                <option value="all">All employees</option>
                {issueAssigneeOptions.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="issues-filter-pills" aria-label="Issue employee filters">
            <button
              className={`issues-filter-pill ${selectedIssueAssignee === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedIssueAssignee('all')}
            >
              All
              <span>{issueTasks.length}</span>
            </button>
            {issueAssigneeOptions.map(user => {
              const count = issueTasks.filter(task => task.assignedTo.includes(user.id)).length;
              return (
                <button
                  key={user.id}
                  className={`issues-filter-pill ${selectedIssueAssignee === user.id ? 'active' : ''}`}
                  onClick={() => setSelectedIssueAssignee(user.id)}
                >
                  {user.name}
                  <span>{count}</span>
                </button>
              );
            })}
          </div>

          <div className="issues-table-wrap">
            <table className="issues-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Task</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredIssueTasks.map(task => (
                  <tr key={task.id} onClick={() => navigate(`/workspace/${task.id}`)}>
                    <td className="issues-code">{task.code}</td>
                    <td className="issues-task">{task.name}</td>
                    <td className="issues-assignees">{task.assignedTo.map(id => getUserName(id)).join(', ')}</td>
                    <td>
                      <span className="badge small" style={{ backgroundColor: TaskStatusColors.ISSUES, color: '#fff' }}>
                        Issues
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alerts (PM only) — grouped by severity */}
      {alerts.length > 0 && (
        <div className="dashboard-card full-width alerts-card">
          <div className="card-header-row">
            <h3 className="card-title">
              <BellIcon size={18} />
              {' '}Alerts
              {unreadAlertCount > 0 && (
                <span className="alert-unread-badge">{unreadAlertCount}</span>
              )}
            </h3>
            {unreadAlertCount > 0 && (
              <button className="view-all-btn" onClick={markAllAlertsRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="alert-groups">
            {alertGroups.map(group => {
              const collapsed = collapsedSeverities.has(group.severity);
              return (
                <section key={group.severity} className={`alert-group sev-${group.severity}`}>
                  <button
                    type="button"
                    className="alert-group-header"
                    aria-expanded={!collapsed}
                    onClick={() => toggleSeverity(group.severity)}
                  >
                    <ChevronRightIcon size={14} className={`alert-group-chevron ${collapsed ? '' : 'open'}`} />
                    <span className="alert-group-title">{SEVERITY_LABELS[group.severity]}</span>
                    <span className="alert-group-count">{group.items.length}</span>
                    {group.unread > 0 && <span className="alert-group-unread">{group.unread} new</span>}
                  </button>

                  {!collapsed && (
                    <div className="alerts-list">
                      {group.items.slice(0, 8).map(alert => (
                        <div
                          key={alert.id}
                          className={`alert-item ${alert.isRead ? '' : 'unread'}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => { markAlertRead(alert.id); navigate(`/workspace/${alert.subElementId}`); }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              markAlertRead(alert.id);
                              navigate(`/workspace/${alert.subElementId}`);
                            }
                          }}
                        >
                          <div className="alert-item-icon">{alertIcon(alert.type)}</div>
                          <div className="alert-item-content">
                            <span className="alert-item-title">
                              <span className="alert-type-tag">{ALERT_META[alert.type].label}</span>
                              {alert.taskCode} — {alert.taskName}
                            </span>
                            <span className="alert-item-message">{alert.message}</span>
                            <span className="alert-item-time">{relativeTime(alert.createdAt)}</span>
                          </div>
                          {!alert.isRead && (
                            <button
                              className="alert-dismiss"
                              onClick={e => { e.stopPropagation(); markAlertRead(alert.id); }}
                              title="Mark as read"
                              aria-label="Mark alert as read"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      {group.items.length > 8 && (
                        <button
                          type="button"
                          className="alert-group-more"
                          onClick={() => navigate('/notifications')}
                        >
                          +{group.items.length - 8} more — view all notifications
                        </button>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      )}

      {/* Gantt Chart (PM only) */}
      <div className="dashboard-card full-width">
        <div className="card-header-row">
          <h3 className="card-title">Project Timeline</h3>
          <button className="view-all-btn" onClick={() => navigate('/calendar')}>
            Full Calendar →
          </button>
        </div>
        <GanttChart
          rows={buildGanttRows(activeProject)}
          projectStartDate={activeProject.startDate}
          projectEndDate={activeProject.endDate}
          onTaskClick={(taskId) => navigate(`/workspace/${taskId}`)}
          maxRows={30}
        />
      </div>

      {/* Recent Activity */}
      <div className="dashboard-card full-width">
        <div className="card-header-row">
          <h3 className="card-title">Recent Activity</h3>
          <button className="view-all-btn" onClick={() => navigate('/workspace')}>
            View Workspace →
          </button>
        </div>
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Task</th>
                <th>Assigned To</th>
                <th>Est. Hours</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {recentTasks.map(task => (
                <tr key={task.id} className="clickable-row" onClick={() => navigate('/workspace')}>
                  <td className="order-number">{task.code}</td>
                  <td className="task-name-cell">{task.name}</td>
                  <td>
                    {task.assignedTo.length > 0 ? (
                      <div className="assignee-cell">
                        {task.assignedTo.slice(0, 3).map(id => (
                          <Avatar key={id} user={resolveUser(id)} name={getUserName(id)} size={24} className="assignee-avatar" title={getUserName(id)} />
                        ))}
                        {task.assignedTo.length > 3 && <span className="assignee-more">+{task.assignedTo.length - 3}</span>}
                        {task.assignedTo.length === 1 && <span className="assignee-name">{getUserName(task.assignedTo[0])}</span>}
                      </div>
                    ) : (
                      <span className="assignee-none">—</span>
                    )}
                  </td>
                  {FEATURES.TIME_TRACKING && <td className="amount">{formatHours(task.estimatedHours)}</td>}
                  <td>
                    <span
                      className="status-pill"
                      style={{ '--status-color': statusChartColor(task.status, chartColors) } as React.CSSProperties}
                    >
                      {TaskStatusLabels[task.status]}
                    </span>
                  </td>
                  <td className="date-cell">{new Date(task.updatedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
